import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import {
  SubscriptionPlan,
  canSelectPlan,
  normalizePlan,
} from '../data/subscriptions';
import type { PaymentProvider } from '../api/billing';
import { useSubscriptionTiers } from '../i18n/useSubscriptionTiers';
import { Button } from './ui/Button';
import { appTheme, pitchTheme } from '../styles/theme';

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const PlansHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  max-width: 640px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.375rem, 2.5vw, 1.625rem);
  font-weight: 500;
  letter-spacing: -0.02em;
  color: ${appTheme.text};
`;

const Intro = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: ${appTheme.textMuted};
`;

const TierGrid = styled.div<{ $compact?: boolean }>`
  display: grid;
  gap: 24px;
  grid-template-columns: ${({ $compact }) =>
    $compact ? 'repeat(auto-fill, minmax(260px, 1fr))' : 'repeat(3, minmax(0, 1fr))'};
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    max-width: ${({ $compact }) => ($compact ? 'none' : '420px')};
    margin: ${({ $compact }) => ($compact ? '0' : '0 auto')};
  }
`;

const TierCard = styled.article<{
  $featured?: boolean;
  $current?: boolean;
  $compact?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 100%;
  padding: 28px 24px 24px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowSm};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  ${({ $current }) =>
    $current &&
    css`
      border-color: ${appTheme.green};
      box-shadow: 0 0 0 1px ${appTheme.green}, ${appTheme.shadowMd};
    `}

  ${({ $featured, $compact }) =>
    $featured &&
    !$compact &&
    css`
      border-color: ${appTheme.green};
      background: linear-gradient(180deg, ${appTheme.mint} 0%, ${appTheme.surface} 42%);
      box-shadow: 0 16px 48px rgba(19, 78, 68, 0.12);

      @media (min-width: 961px) {
        transform: translateY(-6px);
      }
    `}

  &:hover {
    border-color: ${appTheme.green};
    box-shadow: ${appTheme.shadowMd};
  }
`;

const PopularBadge = styled.span`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 6px 14px;
  border-radius: ${appTheme.radiusPill};
  background: ${appTheme.green};
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(19, 78, 68, 0.2);
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 20px;
`;

const TierNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const TierName = styled.h3`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.125rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${appTheme.text};
`;

const CurrentBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.green};
  background: ${appTheme.mint};
  border: 1px solid rgba(19, 78, 68, 0.12);
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
`;

const PriceBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 12px;
`;

const PriceAmount = styled.span`
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(2rem, 4vw, 2.625rem);
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.03em;
  color: ${appTheme.greenDark};
`;

const PricePeriod = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${appTheme.textMuted};
`;

const BillingNote = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 0.75rem;
  color: ${appTheme.textMuted};
`;

const TierDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};
`;

const FeatureDivider = styled.hr`
  margin: 0 0 20px;
  border: none;
  border-top: 1px solid ${appTheme.border};
`;

const FeatureList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${appTheme.text};
`;

const FeatureCheck = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${appTheme.mint};
  color: ${appTheme.green};
  font-size: 0.6875rem;
  font-weight: 700;
`;

const TierAction = styled(Button)`
  margin-top: 24px;
  width: 100%;
`;

const FEATURED_PLAN: SubscriptionPlan = 'basic';

const parsePriceLabel = (
  priceLabel: string,
): { amount: string; isMonthly: boolean } => {
  const slashIndex = priceLabel.indexOf('/');
  if (slashIndex === -1) {
    return { amount: priceLabel, isMonthly: false };
  }
  return {
    amount: priceLabel.slice(0, slashIndex).trim(),
    isMonthly: true,
  };
};

interface SubscriptionPlansProps {
  currentPlan?: string | null;
  compact?: boolean;
  showUpgradeActions?: boolean;
  billingEnabled?: boolean | null;
  paymentProvider?: PaymentProvider | null;
  stripeEnabled?: boolean;
  upgradingPlan?: SubscriptionPlan | null;
  onUpgrade?: (plan: SubscriptionPlan) => void;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  currentPlan,
  compact = false,
  showUpgradeActions = false,
  billingEnabled = null,
  paymentProvider = null,
  stripeEnabled,
  upgradingPlan = null,
  onUpgrade,
}) => {
  const { t } = useTranslation();
  const tiers = useSubscriptionTiers();
  const plan = normalizePlan(currentPlan);
  const checkoutEnabled = billingEnabled ?? stripeEnabled ?? false;
  const billingLoading = billingEnabled === null && stripeEnabled === undefined;
  const providerKey =
    paymentProvider === 'dodo' ? 'dodo' : paymentProvider === 'stripe' ? 'stripe' : 'checkout';
  const providerLabel = t(`subscription.plans.providers.${providerKey}`);

  const actionLabel = (tierId: SubscriptionPlan): string => {
    const status = canSelectPlan(plan, tierId);
    const tierName = tiers.find((tier) => tier.id === tierId)?.name ?? tierId;
    if (status === 'current') {
      return t('subscription.plans.currentPlan');
    }
    if (status === 'upgrade') {
      if (checkoutEnabled && tierId !== 'free') {
        return t('subscription.plans.subscribeTo', { plan: tierName });
      }
      return t('subscription.plans.upgradeTo', { plan: tierName });
    }
    return t('subscription.plans.included');
  };

  return (
    <Panel>
      {!compact && (
        <PlansHeader>
          <Heading>{t('subscription.plans.heading')}</Heading>
          <Intro>
            {t('subscription.plans.intro')}
            {checkoutEnabled
              ? t('subscription.plans.introCheckout', { provider: providerLabel })
              : billingEnabled === false
                ? t('subscription.plans.introDev')
                : ''}
          </Intro>
        </PlansHeader>
      )}
      <TierGrid $compact={compact}>
        {tiers.map((tier) => {
          const status = canSelectPlan(plan, tier.id);
          const isUpgrading = upgradingPlan === tier.id;
          const isActive = tier.id === plan;
          const isFeatured = tier.id === FEATURED_PLAN && !compact;
          const { amount, isMonthly } = parsePriceLabel(tier.priceLabel);

          return (
            <TierCard
              key={tier.id}
              $featured={isFeatured}
              $current={isActive}
              $compact={compact}
            >
              {isFeatured && !isActive && (
                <PopularBadge>{t('subscription.plans.mostPopular')}</PopularBadge>
              )}

              <CardHeader>
                <TierNameRow>
                  <TierName>{tier.name}</TierName>
                  {isActive && <CurrentBadge>{t('subscription.plans.yourPlan')}</CurrentBadge>}
                </TierNameRow>
                <PriceBlock>
                  <PriceAmount>{amount}</PriceAmount>
                  {isMonthly && (
                    <PricePeriod>{t('subscription.plans.perMonth')}</PricePeriod>
                  )}
                </PriceBlock>
                {tier.id === 'free' ? (
                  <BillingNote>{t('subscription.plans.freeForever')}</BillingNote>
                ) : (
                  isMonthly && (
                    <BillingNote>{t('subscription.plans.billedMonthly')}</BillingNote>
                  )
                )}
                <TierDescription>{tier.description}</TierDescription>
              </CardHeader>

              <FeatureDivider />

              <FeatureList>
                {tier.features.map((feature) => (
                  <FeatureItem key={feature}>
                    <FeatureCheck aria-hidden="true">✓</FeatureCheck>
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>

              {showUpgradeActions && (
                <TierAction
                  type="button"
                  $variant={status === 'upgrade' ? 'primary' : 'secondary'}
                  $size="lg"
                  disabled={
                    status !== 'upgrade' ||
                    isUpgrading ||
                    Boolean(upgradingPlan) ||
                    billingLoading
                  }
                  onClick={() => {
                    if (status === 'upgrade' && onUpgrade) {
                      onUpgrade(tier.id);
                    }
                  }}
                >
                  {isUpgrading
                    ? checkoutEnabled
                      ? t('subscription.plans.startingCheckout')
                      : t('subscription.plans.upgrading')
                    : actionLabel(tier.id)}
                </TierAction>
              )}
            </TierCard>
          );
        })}
      </TierGrid>
    </Panel>
  );
};

export default SubscriptionPlans;
